import type { ProviderFactoryWithOptions, ProviderFactoryConfig } from '@standardagents/spec';
import { AnthropicProvider } from './AnthropicProvider';
import { anthropicProviderOptions } from './providerOptions';

/**
 * Anthropic (Claude) provider factory for Standard Agents
 *
 * Includes typed providerOptions for Anthropic-specific configuration.
 *
 * @example
 * ```typescript
 * import { defineModel } from '@standardagents/builder';
 * import { anthropic } from '@standardagents/anthropic';
 *
 * export default defineModel({
 *   name: 'claude-opus',
 *   provider: anthropic,
 *   model: 'claude-opus-4-8',
 *   inputPrice: 5,
 *   outputPrice: 25,
 *   capabilities: {
 *     supportsImages: true,
 *     supportsToolCalls: true,
 *     supportsJsonMode: true,
 *     maxContextTokens: 1000000,
 *   },
 *   providerOptions: {
 *     cacheSystemPrompt: true,
 *   },
 * });
 * ```
 */
export const anthropic: ProviderFactoryWithOptions<typeof anthropicProviderOptions> =
  Object.assign(
    (config: ProviderFactoryConfig) => new AnthropicProvider(config),
    { providerOptions: anthropicProviderOptions }
  );

// Re-export the provider options schema and types
export { anthropicProviderOptions, type AnthropicProviderOptions } from './providerOptions';

// Re-export the provider class for advanced usage
export { AnthropicProvider } from './AnthropicProvider';

// Re-export pricing helpers for cost-aware consumers
export {
  getAnthropicModelPricing,
  calculateAnthropicUsageCost,
  extractUsageBuckets,
  type AnthropicModelPricing,
  type AnthropicUsageBuckets,
} from './pricing';
